import React, { useEffect, useRef, useState } from 'react';
import { getMapboxToken } from '../../utils/utils';
import { ordersAPI } from '../../config/orders-api';

const mapboxgl = window.mapboxgl;

const OrdersMapView = ({ open, filters = {}, driverName, onStatsLoaded }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onStatsLoadedRef = useRef(onStatsLoaded);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const token = getMapboxToken();

  useEffect(() => {
    onStatsLoadedRef.current = onStatsLoaded;
  }, [onStatsLoaded]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setOrders([]);

      const response = await ordersAPI.getOrdersMap({
        status: filters.status || undefined,
        search: filters.search || undefined,
        filterType: filters.filterType,
        date: filters.filterType === 'date' ? filters.date : undefined,
        month: filters.filterType === 'month' ? filters.month : undefined,
        year: filters.filterType === 'month' ? filters.year : undefined,
        startDate: filters.filterType === 'range' ? filters.startDate : undefined,
        endDate: filters.filterType === 'range' ? filters.endDate : undefined,
        driverId: filters.driverId !== 'All' ? filters.driverId : undefined,
        courierPartner: filters.courierPartner !== 'All' ? filters.courierPartner : undefined,
        timeSlot: filters.timeSlot !== 'All' ? filters.timeSlot : undefined,
        adminId: filters.adminId !== 'All' ? filters.adminId : undefined,
        orderType: filters.orderType && filters.orderType !== 'all' ? filters.orderType : undefined,
        productId: filters.productId || undefined,
        productNumericId: filters.productNumericId || undefined,
      });

      if (cancelled) return;

      if (response.success) {
        setOrders(response.data || []);
        if (response.stats && onStatsLoadedRef.current) {
          onStatsLoadedRef.current(response.stats);
        }
      } else {
        setError(response.error || 'Failed to load map orders');
      }
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, filters]);

  useEffect(() => {
    if (!open || !token || !containerRef.current || !mapboxgl) return undefined;

    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [51.531, 25.2854],
      zoom: 11,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    const onLoad = () => {
      setMapReady(true);
      map.resize();
    };

    if (map.isStyleLoaded()) {
      onLoad();
    } else {
      map.once('load', onLoad);
    }

    const onResize = () => map.resize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      setMapReady(false);
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [open, token]);

  useEffect(() => {
    const map = mapRef.current;
    if (!open || !map || !mapReady) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const bounds = new mapboxgl.LngLatBounds();
    let hasPoint = false;

    orders.forEach((order) => {
      const lng = Number(order.lng);
      const lat = Number(order.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

      const el = document.createElement('div');
      el.className = 'orders-map-marker';
      el.innerHTML = `<span class="orders-map-marker-label">${order.orderId || '?'}</span>`;

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([lng, lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 18 }).setHTML(
            `<strong>#${order.orderId || ''}</strong>${order.orderStatus ? `<br/>${order.orderStatus}` : ''}`
          )
        )
        .addTo(map);

      markersRef.current.push(marker);
      bounds.extend([lng, lat]);
      hasPoint = true;
    });

    if (hasPoint) {
      map.fitBounds(bounds, { padding: 64, maxZoom: 14 });
    }

    requestAnimationFrame(() => map.resize());
  }, [orders, mapReady, open]);

  if (!open) return null;

  return (
    <div className="orders-map-inline" aria-label="Orders map view">
      <div className="orders-map-header">
        <div>
          <h2>Map View</h2>
          <p>
            {driverName ? `${driverName} · ` : ''}
            {loading ? 'Loading…' : `${orders.length} order${orders.length === 1 ? '' : 's'} with location`}
          </p>
        </div>
      </div>

      <div className="orders-map-body">
        {!token && (
          <div className="orders-map-state">Mapbox token missing</div>
        )}
        {token && loading && (
          <div className="orders-map-state">Loading orders…</div>
        )}
        {token && !loading && error && (
          <div className="orders-map-state orders-map-state-error">{error}</div>
        )}
        {token && !loading && !error && orders.length === 0 && (
          <div className="orders-map-state">No orders with coordinates for this driver</div>
        )}
        <div ref={containerRef} className="orders-map-canvas" />
      </div>
    </div>
  );
};

export default OrdersMapView;
